// PATCH /admin/dataengine/reconcile/floorplan_unit/{id} dialog.
//
// The form only allows editing the engineering-defended whitelist
// (unit_type, n_storeys, bd/ba, areas, classification, property_type,
// facing). URA-canonical fields are deliberately absent so the operator
// can't accidentally drift them through this surface.
//
// Submit diffs against the current snapshot: only changed keys go over
// the wire. Empty *string fields are sent as "" so the backend can
// coerce to NULL (drop classification, etc.); numeric fields that are
// not modified are simply omitted from the patch.

import { useMemo, useState, type CSSProperties } from "react";
import { Modal } from "@/components/ui-prims";
import { inputBase } from "@/components/ui-prims";
import { ghostBtn, primaryBtn } from "@/components/admin-ui";

export type FloorplanUnitSnapshot = {
  id: number;
  unit_type: string;
  classification?: string | null;
  property_type?: string | null;
  n_storeys: number;
  bedrooms?: number | null;
  bathrooms?: number | null;
  total_area_sqm?: number | null;
  balcony_sqm?: number | null;
  facing?: string | null;
};

export type UnitPatchBody = {
  unit_type?: string;
  classification?: string;
  property_type?: string;
  n_storeys?: number;
  bedrooms?: number;
  bathrooms?: number;
  total_area_sqm?: number;
  balcony_sqm?: number;
  facing?: string;
  reason: string;
};

interface Props {
  open: boolean;
  unit: FloorplanUnitSnapshot | null;
  busy: boolean;
  onSubmit: (body: UnitPatchBody) => void;
  onCancel: () => void;
}

const CLASSIFICATIONS = ["", "per_unit_plan", "floor_plate"] as const;

export function EditUnitDialog({ open, unit, busy, onSubmit, onCancel }: Props) {
  // The dialog is intentionally unmounted between opens (we re-key on
  // unit.id) so we don't have to manually sync state from props.
  return (
    <Modal
      open={open && unit != null}
      onClose={busy ? () => {} : onCancel}
      title={unit ? `Edit unit · ${unit.unit_type || "—"}` : "Edit unit"}
      width={520}
    >
      {unit && (
        <EditUnitForm
          key={unit.id}
          unit={unit}
          busy={busy}
          onSubmit={onSubmit}
          onCancel={onCancel}
        />
      )}
    </Modal>
  );
}

function EditUnitForm({ unit, busy, onSubmit, onCancel }: { unit: FloorplanUnitSnapshot; busy: boolean; onSubmit: Props["onSubmit"]; onCancel: () => void }) {
  const [unitType, setUnitType] = useState(unit.unit_type ?? "");
  const [classification, setClassification] = useState(unit.classification ?? "");
  const [propertyType, setPropertyType] = useState(unit.property_type ?? "");
  const [nStoreys, setNStoreys] = useState(String(unit.n_storeys ?? 1));
  const [bedrooms, setBedrooms] = useState(unit.bedrooms == null ? "" : String(unit.bedrooms));
  const [bathrooms, setBathrooms] = useState(unit.bathrooms == null ? "" : String(unit.bathrooms));
  const [totalArea, setTotalArea] = useState(unit.total_area_sqm == null ? "" : String(unit.total_area_sqm));
  const [balcony, setBalcony] = useState(unit.balcony_sqm == null ? "" : String(unit.balcony_sqm));
  const [facing, setFacing] = useState(unit.facing ?? "");
  const [reason, setReason] = useState("");

  // Diff against the snapshot. Only keys that differ are included in
  // the patch payload — keeps the audit row honest and avoids touching
  // updated_at on no-op submits.
  const patch = useMemo<Omit<UnitPatchBody, "reason">>(() => {
    const p: Omit<UnitPatchBody, "reason"> = {};
    const trimmedUT = unitType.trim();
    if (trimmedUT !== (unit.unit_type ?? "")) p.unit_type = trimmedUT;
    if ((classification ?? "") !== (unit.classification ?? "")) p.classification = classification;
    if ((propertyType ?? "") !== (unit.property_type ?? "")) p.property_type = propertyType;

    const ns = parseInt(nStoreys, 10);
    if (!Number.isNaN(ns) && ns !== unit.n_storeys) p.n_storeys = ns;

    parseAndSetNumber(bedrooms, unit.bedrooms, (v) => { p.bedrooms = v; });
    parseAndSetNumber(bathrooms, unit.bathrooms, (v) => { p.bathrooms = v; });
    parseAndSetFloat(totalArea,  unit.total_area_sqm, (v) => { p.total_area_sqm = v; });
    parseAndSetFloat(balcony,    unit.balcony_sqm,    (v) => { p.balcony_sqm = v; });

    if ((facing ?? "") !== (unit.facing ?? "")) p.facing = facing;
    return p;
  }, [
    unit, unitType, classification, propertyType, nStoreys,
    bedrooms, bathrooms, totalArea, balcony, facing,
  ]);

  const changedKeys = Object.keys(patch);
  const reasonOk = reason.trim().length > 0;
  const utEmpty = unitType.trim() === "";
  const submittable = !busy && changedKeys.length > 0 && reasonOk && !utEmpty;

  function submit() {
    if (!submittable) return;
    onSubmit({ ...patch, reason: reason.trim() });
  }

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label="Unit type" required>
          <input style={inputBase} value={unitType} onChange={(e) => setUnitType(e.target.value)} disabled={busy} />
        </Field>
        <Field label="Storeys" hint="1–4">
          <input style={inputBase} type="number" min={1} max={4} value={nStoreys} onChange={(e) => setNStoreys(e.target.value)} disabled={busy} />
        </Field>
        <Field label="Bedrooms">
          <input style={inputBase} type="number" min={0} value={bedrooms} onChange={(e) => setBedrooms(e.target.value)} disabled={busy} />
        </Field>
        <Field label="Bathrooms">
          <input style={inputBase} type="number" min={0} value={bathrooms} onChange={(e) => setBathrooms(e.target.value)} disabled={busy} />
        </Field>
        <Field label="Total area (m²)">
          <input style={inputBase} type="number" min={0} step={0.1} value={totalArea} onChange={(e) => setTotalArea(e.target.value)} disabled={busy} />
        </Field>
        <Field label="Balcony (m²)">
          <input style={inputBase} type="number" min={0} step={0.1} value={balcony} onChange={(e) => setBalcony(e.target.value)} disabled={busy} />
        </Field>
        <Field label="Classification">
          <select style={inputBase} value={classification} onChange={(e) => setClassification(e.target.value)} disabled={busy}>
            {CLASSIFICATIONS.map((c) => (
              <option key={c} value={c}>{c === "" ? "—" : c}</option>
            ))}
          </select>
        </Field>
        <Field label="Property type">
          <input style={inputBase} value={propertyType} onChange={(e) => setPropertyType(e.target.value)} disabled={busy} />
        </Field>
        <Field label="Facing" wide>
          <input style={inputBase} value={facing} onChange={(e) => setFacing(e.target.value)} disabled={busy} />
        </Field>
      </div>

      <div style={{ marginTop: 16 }}>
        <Field label="Reason" required wide hint="Audit log requires a non-empty reason on every edit.">
          <input
            style={inputBase}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. extractor merged two unit_types — splitting per brochure"
            disabled={busy}
          />
        </Field>
      </div>

      <div style={{ marginTop: 14, fontSize: 11.5, color: "var(--ink-4)", fontFamily: "var(--font-mono)" }}>
        {changedKeys.length === 0
          ? "No changes yet — edit a field to enable Save."
          : `Will patch ${changedKeys.length} field${changedKeys.length === 1 ? "" : "s"}: ${changedKeys.join(", ")}`}
      </div>

      <div style={{ marginTop: 18, display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button type="button" style={ghostBtn} onClick={onCancel} disabled={busy}>Cancel</button>
        <button
          type="button"
          style={{ ...primaryBtn, opacity: submittable ? 1 : 0.5, cursor: submittable ? "pointer" : "not-allowed" }}
          onClick={submit}
          disabled={!submittable}
        >
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </>
  );
}

function Field({ label, required, hint, wide, children }: { label: string; required?: boolean; hint?: string; wide?: boolean; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, gridColumn: wide ? "1 / -1" : undefined }}>
      <span style={fieldLabel}>
        {label}{required ? " *" : ""}
        {hint && <span style={hintStyle}> · {hint}</span>}
      </span>
      {children}
    </label>
  );
}

// parseAndSetNumber and parseAndSetFloat: blank input = "leave it alone".
// Clearing a numeric value back to NULL is not yet supported through
// this surface — the operator workflow is correcting extraction values,
// not deleting them.
function parseAndSetNumber(s: string, current: number | null | undefined, push: (v: number) => void) {
  const t = s.trim();
  if (t === "") return;
  const v = parseInt(t, 10);
  if (Number.isNaN(v)) return;
  if (v !== (current ?? null)) push(v);
}

function parseAndSetFloat(s: string, current: number | null | undefined, push: (v: number) => void) {
  const t = s.trim();
  if (t === "") return;
  const v = parseFloat(t);
  if (Number.isNaN(v)) return;
  if (v !== (current ?? null)) push(v);
}

const fieldLabel: CSSProperties = {
  fontFamily: "var(--font-ui)", fontSize: 10.5, fontWeight: 600,
  letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--ink-4)",
};
const hintStyle: CSSProperties = {
  textTransform: "none", letterSpacing: 0, fontWeight: 400, color: "var(--ink-4)",
};
